import { defineStore } from 'pinia'
import { useAppStore } from '@/store/modules/app'
import { getJson } from '@/utils/tools.js'
import { isJSON } from '@/utils/is.js'
import useApi from '@/api/live'
import { useStorage } from '@/hooks/web/useStorage'
const { getStorage } = useStorage()

/** 获取数组中最接近目标值且不大于目标值的数字（前面那个） */
export const getClosestPrevNumber = (arr: number[], target: number): number | undefined => {
  if (!arr.length) return undefined
  let result: number | undefined
  for (const num of arr) {
    if (num <= target && (result === undefined || num > result)) {
      result = num
    }
  }
  return result
}

/** 获取对象中与 key 匹配及 key 之前的数据 */
export const getObjDataBeforeKey = <T extends Record<string, unknown>>(
  obj: T,
  key: string | number
): Partial<T> => {
  const result = {} as Partial<T>
  const target = String(key)
  for (const k of Object.keys(obj)) {
    result[k as keyof T] = obj[k as keyof T]
    if (k === target) break
  }
  return result
}

/** 处理对应商品状态 */
const handleProductStatus = (productInfo: any, msgType: string, msgData: any) => {
  const appStore = useAppStore()
  switch(msgType) {
    // 商品上架
    case 'ROOM_PRODUCT_ON_SALE':
      productInfo.putawayStatus = 1
      break
    // 商品下架
    case 'ROOM_PRODUCT_OFF_SALE':
      productInfo.putawayStatus = 0
      productInfo.recommendStatus = 0
      break
    // 商品推荐
    case 'ROOM_PRODUCT_RECOMMEND':
      productInfo.putawayStatus = 1
      productInfo.recommendStatus = 1
      appStore.setRecommend({...productInfo, ...msgData})
      appStore.setRecommendStatus(true)
      break
    // 商品取消推荐
    case 'ROOM_PRODUCT_UN_RECOMMEND':
      productInfo.recommendStatus = 0
      appStore.setRecommendStatus(false)
      appStore.setRecommend({})
      break
  }
  return productInfo
}

/** 获取回放商品列表 */
const getRoomRecordProductList = async () => {
    try {
    const res: any = await useApi.getRoomRecordProductList({
      roomId: getStorage('roomId')
    })
    const { code, data } = res
    if(code !== 200) return []
    const list = data.filter((item: any) => item.productStatus === 1).map((item: any, index: number) => ({
      ...item,
      index,
      putawayStatus: item.productInitStatus === 1 ? 1 : 0, // 上架状态
      recommendStatus: 0, // 推荐状态
    }))
    return list
  } catch (error) {
    return []
  }
}

export interface IProductBackItem {
  id?: string | number
  productId: string | number
  productName?: string
  productPics?: string
  productPrice?: number | string
  productLinePrice?: number | string
  saleCount?: number
  stock?: number
  soldOut?: number
  recommended?: number
  index?: number
}

export interface IProductBackRecommend {
  show: boolean
  recommendType?: number
  productId?: string | number
  productName?: string
  productPrice?: number | string
  productLinePrice?: number | string
  saleCount?: number
  stock?: number
  hotSaleNumber?: number
  soldOut?: number
}

export const useProductBackStore = defineStore('productBack', {
  state: () => {
    return {
      // 回放商品初始列表
      productBackList: [] as any[],
      // 回放商品消息列表
      productBackMessage: {} as any,
      // 回放商品是否开启
      productBackStatus: false,
      // 当前商品展示时间节点
      currentProductTimeNode: 0
    }
  },

  actions: {
    // 设置回放商品初始化数据
    async setProductBackMessage() {
      console.log('商品推荐状态true')
      if(this.productBackStatus) return
      this.productBackStatus = true
      const appStore = useAppStore()
      this.productBackList = await getRoomRecordProductList()
      const showCommodityList = this.productBackList.filter((item: any) => item.putawayStatus === 1).map((item: any, index: number) => ({
        ...item,
        index,
      }))
      appStore.setCommodityList(showCommodityList)
      appStore.setCommodityConfig({})
      appStore.setRecommend({})
      appStore.setRecommendStatus(false)
      const _url = appStore.getConfig.resourceUrl + '/files/' + appStore.getConfig.clientId + '/message/' + appStore.getConfig.playbackVideoCode + '/product.json'
      const res: any = await getJson(_url)
      console.log('回放商品消息', res)
      if (res) {
        this.productBackMessage = res
      }
    },

    // 读取回放商品消息
    async readProductBackMessage(time: number) {
      if(!this.productBackStatus) return
      const appStore = useAppStore()
      const list = Object.keys(this.productBackMessage).map(Number)
      const closestTime = getClosestPrevNumber(list, time)
      console.log('closestTime', closestTime)
      if(!closestTime){
        this.currentProductTimeNode = 0
        appStore.setCommodityList(this.productBackList.filter((item: any) => item.putawayStatus === 1))
        appStore.setRecommend({})
        appStore.setRecommendStatus(false)
        return
      }
      if(closestTime === this.currentProductTimeNode) return
      this.currentProductTimeNode = closestTime
      const msgList = getObjDataBeforeKey(this.productBackMessage, closestTime)
      console.log('closestTime1', msgList)
      let productList = JSON.parse(JSON.stringify(this.productBackList))
      for(const item in msgList) {
        msgList[item].forEach((em: any) => {
          if(isJSON(em?.message)) {
            const info = JSON.parse(em?.message)
            const { Desc, Data } = info
            if(!isJSON(Data)) return
            const { productId, recommendType } = JSON.parse(Data)
            const i = productList.findIndex((item: any) => item.productId === productId)
            if(i === -1) return
            const newProduct = handleProductStatus(productList[i], Desc, {recommendType})
            productList[i] = newProduct
          }
        })
      }
      const showCommodityList = productList.filter((item: any) => item.putawayStatus === 1).map((item: any, index: number) => ({
        ...item,
        index,
      }))
      if(!showCommodityList.some((item: any) => item.recommendStatus === 1)) {
        appStore.setRecommend({})
        appStore.setRecommendStatus(false)
      }
      appStore.setCommodityList(showCommodityList)
    },

    // 停止回放商品时数据重置
    resetProductBackMessage() {
      console.log('商品推荐状态false')
      if(!this.productBackStatus) return
      this.productBackStatus = false
      this.productBackList = []
      this.productBackMessage = {}
      this.currentProductTimeNode = 0
      useAppStore().setCommodityList([])
      useAppStore().setRecommend({})
      useAppStore().setRecommendStatus(false)
    }
  }
})
